import React, { useEffect, useState, useCallback } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import ToastContainer from "../common/ToastContainer";
import { BRAND } from "../../branding";
import MobileNavDrawer from "../responsive-fixes/MobileNavDrawer";
import { useResponsiveLayout } from "../responsive-fixes/useResponsiveLayout";
import LockdownBannerMobile from "../responsive-fixes/LockdownBannerMobile";
import FooterMobile from "../responsive-fixes/FooterMobile";

interface LayoutProps {
  children: React.ReactNode;
  user?: { name: string; role: string; phone: string };
  onLogout?: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, user, onLogout }) => {
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    // Try to get from localStorage, default to false
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('afaq-sidebar-expanded');
      return saved === 'true';
    }
    return false;
  });
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lockdown, setLockdown] = useState<{ locked: boolean; reason?: string; message?: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const responsive = useResponsiveLayout();

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Save sidebar state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('afaq-sidebar-expanded', String(sidebarExpanded));
    }
  }, [sidebarExpanded]);

  // Check lockdown status with error handling and retry
  const checkLockdownStatus = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch("/api/system/lockdown-status", {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        setLockdown(data);
      } else {
        console.error("Lockdown status check failed:", response.status);
        // Retry after 5 seconds on failure
        setTimeout(checkLockdownStatus, 5000);
      }
    } catch (error) {
      if ((error as Error)?.name !== 'AbortError') {
        console.error("Lockdown status check error:", error);
        // Retry after 10 seconds on error
        setTimeout(checkLockdownStatus, 10000);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkLockdownStatus();
    const interval = setInterval(checkLockdownStatus, 30000);
    return () => clearInterval(interval);
  }, [checkLockdownStatus]);

  const toggleSidebar = useCallback(() => {
    // Below 900px the desktop sidebar is display:none (layout.css:561) — the
    // toggle opens the off-canvas drawer instead. At >= 900px the toggle
    // expands/collapses the sidebar as before.
    if (!responsive.sidebarVisible) {
      setDrawerOpen(prev => !prev);
    } else {
      setSidebarExpanded(prev => !prev);
    }
  }, [responsive.sidebarVisible]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-AE", { 
      hour: "2-digit", 
      minute: "2-digit", 
      timeZone: "Asia/Dubai",
      hour12: false,
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-AE", {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "Asia/Dubai",
    });
  };

  if (isLoading) {
    return (
      <div style={{
        background: "#060e20",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#bdb2ff",
        fontFamily: BRAND.fontFamily,
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ 
            width: 40, 
            height: 40, 
            border: "3px solid rgba(189, 178, 255, 0.1)",
            borderTop: "3px solid #bdb2ff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 16px",
          }} />
          <div style={{ fontSize: 16, fontWeight: 600 }}>Loading {BRAND.name}...</div>
          <div style={{ fontSize: 13, color: "rgba(221, 229, 255, 0.4)", marginTop: 8 }}>
            Initializing Enterprise Intelligence
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="afaq-layout" style={{ 
      background: "#060e20", 
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    }}>
      {/* Lockdown Banner — M5 fix (audit PR #51): on phone, render the
          stacked LockdownBannerMobile so a long reason + email don't
          wrap the row to ~5 lines. Desktop banner unchanged. */}
      {lockdown?.locked && (
        responsive.isPhone ? (
          <LockdownBannerMobile
            reason={lockdown.reason}
            onDismiss={() => setLockdown(null)}
          />
        ) : (
          <div
            className="lockdown-banner"
            role="alert"
            style={{
              background: "rgba(255,107,107,0.1)",
              border: "0.5px solid rgba(255,107,107,0.3)",
              padding: "12px 24px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              fontFamily: BRAND.fontFamily,
              fontSize: 13,
              color: "#ff6b6b",
              zIndex: 200,
              position: "relative",
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>lock</span>
            <div style={{ flex: 1 }}>
              <strong>SYSTEM LOCKDOWN</strong>
              {lockdown.reason && ` â€” ${lockdown.reason}`}
              {" "}Â· Contact:{" "}
              <a
                href="mailto:m.masoomi@gmail.com"
                style={{
                  color: "#ff6b6b",
                  textDecoration: "underline",
                  fontWeight: 600,
                }}
              >
                m.masoomi@gmail.com
              </a>
            </div>
            <button
              onClick={() => setLockdown(null)}
              style={{
                background: "rgba(255,107,107,0.2)",
                border: "0.5px solid rgba(255,107,107,0.3)",
                borderRadius: 6,
                color: "#ff6b6b",
                padding: "4px 12px",
                cursor: "pointer",
                fontFamily: BRAND.fontFamily,
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              Dismiss
            </button>
          </div>
        )
      )}

      {/* Header */}
      <Header 
        user={user} 
        onLogout={onLogout} 
        onToggleSidebar={toggleSidebar}
        sidebarExpanded={sidebarExpanded}
      />
      
      {/* Toast Notifications */}
      <ToastContainer />
      
      {/* Main Content */}
      <main className="afaq-main" style={{ 
        display: "flex", 
        flex: 1,
        overflow: "hidden",
      }}>
        {/* Sidebar */}
        <Sidebar />
        
        {/* Content Area */}
        <div
          className="afaq-content afaq-main-content"
          style={{
            flex: 1,
            // C3 fix (audit PR #51): below 900px the sidebar is hidden by
            // CSS, so leave no dead margin on the left. At >= 900px keep
            // the existing 80/280 collapsed/expanded behaviour.
            marginLeft: responsive.sidebarVisible ? (sidebarExpanded ? 280 : 80) : 0,
            transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1)",
            overflowY: "auto",
            // m1 fix: phone padding is tighter so 28px horizontal doesn't
            // eat half a 320px viewport.
            padding: responsive.isPhone ? "16px 14px 80px" : "24px 32px 100px",
            background: "linear-gradient(180deg, rgba(6,14,32,0.9) 0%, rgba(6,14,32,1) 100%)",
          }}
        >
          {children}
        </div>
      </main>
      
      {/* Majestic 2027 Footer */}
      <footer style={{
        position: "fixed",
        bottom: 0,
        // C3 fix (audit PR #51): footer also tracks the responsive
        // sidebar offset — no dead 80px column below 900px.
        left: responsive.sidebarVisible ? (sidebarExpanded ? 280 : 80) : 0,
        right: 0,
        zIndex: 30,
        background: "rgba(6, 14, 32, 0.95)",
        backdropFilter: "blur(20px)",
        borderTop: "0.5px solid rgba(189,178,255,0.06)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: responsive.isPhone ? "8px 14px" : "10px 32px",
        transition: "left 0.3s cubic-bezier(0.4,0,0.2,1)",
      }}>
        <div style={{ 
          fontFamily: BRAND.fontFamily, 
          fontSize: 11, 
          fontWeight: 600, 
          textTransform: "uppercase", 
          letterSpacing: "0.12em", 
          color: "#cfeda2", 
          display: "flex", 
          alignItems: "center", 
          gap: 8 
        }}>
          <span 
            className="m27-dot m27-dot--secondary" 
            style={{ 
              width: 6, 
              height: 6, 
              animation: "m27-sonic-pulse 2s ease-out infinite",
              backgroundColor: "#cfeda2",
              borderRadius: "50%",
            }} 
          />
          {BRAND.name} Intelligence 2027 Â· System Health: Optimal
        </div>

        {/* M1 fix (audit PR #51 + Phase-2 second PR): on phone the 5-chip
            row was previously hidden entirely; now we render the
            FooterMobile compact cluster (time + VAT) so the bar isn't
            blank on small viewports. Desktop chips unchanged. */}
        {responsive.isPhone && (
          <FooterMobile time={currentTime} />
        )}
        <div style={{ display: responsive.isPhone ? "none" : "flex", gap: 32, alignItems: "center" }}>
          {/* Live Time */}
          <div style={{ 
            fontFamily: BRAND.fontFamily, 
            fontSize: 11, 
            fontWeight: 600, 
            textTransform: "uppercase", 
            letterSpacing: "0.1em", 
            color: "#bdb2ff",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>schedule</span>
            {formatTime(currentTime)} GST
          </div>
          
          {/* Date */}
          <div style={{ 
            fontFamily: BRAND.fontFamily, 
            fontSize: 11, 
            fontWeight: 600, 
            textTransform: "uppercase", 
            letterSpacing: "0.1em", 
            color: "rgba(221,229,255,0.4)",
          }}>
            {formatDate(currentTime)}
          </div>
          
          {/* Telemetry */}
          <div style={{ 
            fontFamily: BRAND.fontFamily, 
            fontSize: 11, 
            fontWeight: 600, 
            textTransform: "uppercase", 
            letterSpacing: "0.1em", 
            color: "rgba(221,229,255,0.25)",
            cursor: "pointer",
            transition: "color 0.2s",
          }}
          onMouseEnter={e => (e.currentTarget.style.color = "#bdb2ff")}
          onMouseLeave={e => (e.currentTarget.style.color = "rgba(221,229,255,0.25)")}
          >
            Telemetry
          </div>
          
          {/* Node Status */}
          <div style={{ 
            fontFamily: BRAND.fontFamily, 
            fontSize: 11, 
            fontWeight: 600, 
            textTransform: "uppercase", 
            letterSpacing: "0.1em", 
            color: "rgba(221,229,255,0.25)",
            cursor: "pointer",
            transition: "color 0.2s",
          }}
          onMouseEnter={e => (e.currentTarget.style.color = "#bdb2ff")}
          onMouseLeave={e => (e.currentTarget.style.color = "rgba(221,229,255,0.25)")}
          >
            Node Status
          </div>
          
          {/* VAT Indicator */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "rgba(189, 178, 255, 0.06)",
            borderRadius: 6,
            padding: "4px 10px",
          }}>
            <span style={{ 
              fontFamily: BRAND.fontFamily, 
              fontSize: 10, 
              fontWeight: 700, 
              color: "#bdb2ff",
              letterSpacing: "0.05em",
            }}>
              VAT 5%
            </span>
          </div>
        </div>
      </footer>

      {/* C5 fix (audit PR #51): the desktop Header's quick-nav row
          (.header-nav) overflows below ~860px. We scope a media-query
          to hide it on phone+phablet+tablet. The header-nav links are
          available via the MobileNavDrawer below. The principal
          stylesheets (majestic.css, layout.css) are NOT touched — this
          is a runtime style block scoped to the Layout shell. */}
      <style>{`
        @media (max-width: 1023px) {
          .header-nav { display: none !important; }
        }
        @media (max-width: 479px) {
          .header-account .user-menu-container button > span:nth-child(3) {
            /* Hide the role-pill on the user menu button on tiny phones
               so the header doesn't wrap. The chevron and first-name
               stay. */
            display: none;
          }
        }
      `}</style>

      {/* C1 + C2 fix (audit PR #51): off-canvas drawer that overlays
          the page on touch / narrow viewports. Toggle is the existing
          header hamburger; toggleSidebar() switches between desktop
          sidebar expand (>=900px) and drawer open (<900px). */}
      <MobileNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
};

export default Layout;
